import { Plus, Pencil, Download, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { useDebounce } from 'use-debounce'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalAddBatch from '../../../components/modal/batch/ModalAddBatch'
import ModalDetailBatch from '../../../components/modal/batch/ModalDetailBatch'
import ModalEditBatch from '../../../components/modal/batch/ModalEditBatch'
import api from '../../../config/AxiosInstance'
import { convertDateWithoutTime } from '../../../helpers/constants'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const initialMeta = {
  per_page: 10,
  current_page: 1,
  total_row: 0,
  total_page: 0
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const tableHeaderStyle = {
  backgroundColor: '#f8f9fa',
  color: '#333333',
  fontWeight: '700',
  borderBottom: '1px solid #e9ecef',
  textTransform: 'uppercase',
  fontSize: '0.85rem'
}

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const MasterBatchPage = () => {
  const [showModalAdd, setShowModalAdd] = useState(false)
  const [showModalUpdate, setShowModalUpdate] = useState(false)
  const [showModalEdit, setShowModalEdit] = useState(false)
  const [isAdd, setIsAdd] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const [batches, setBatches] = useState([])
  const [meta, setMeta] = useState(initialMeta)
  const [editId, setEditId] = useState(0)
  const [loading, setLoading] = useState(false)
  const [downloadingRows, setDownloadingRows] = useState({})

  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query, 1000)

  const { per_page, current_page } = meta

  const prevQuery = useRef(debouncedQuery)
  const abortControllerRef = useRef(null)

  useEffect(() => {
    const queryChanged = prevQuery.current !== debouncedQuery
    if (queryChanged) {
      prevQuery.current = debouncedQuery
      if (current_page !== 1) {
        setMeta((prev) => ({ ...prev, current_page: 1 }))
        return
      }
    }

    fetchBatches()

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, isAdd, isUpdate, per_page, current_page])

  async function fetchBatches() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setLoading(true)
    try {
      const params = {
        q: debouncedQuery || '',
        per_page,
        page: current_page
      }
      const response = await api.get('master/batch', { params, signal })
      const { data, metadata } = response.data
      setBatches(data)
      setMeta(metadata)
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  const handleDownloadCoa = async (id) => {
    try {
      setDownloadingRows((prev) => ({ ...prev, [id]: true }))
      const response = await api.get(`master/batch/${id}/download-coa`, {
        responseType: 'arraybuffer'
      })

      const contentType = response.headers['content-type']
      if (contentType && contentType.includes('application/json')) {
        const text = new TextDecoder().decode(response.data)
        const json = JSON.parse(text)
        throw new Error(json.errors?.message || 'An error occurred')
      }

      const { data } = response
      const blob = new Blob([data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `coa-batch-${id}.pdf`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      let errMsg = 'An error occurred'
      if (err.response && err.response.data instanceof ArrayBuffer) {
        try {
          const text = new TextDecoder().decode(err.response.data)
          const json = JSON.parse(text)
          errMsg = json.errors?.message || json.message || errMsg
        } catch (e) {
          // ignore parsing error
        }
      } else {
        errMsg = err?.response?.data?.errors?.message || err.message || errMsg
      }

      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setDownloadingRows((prev) => ({ ...prev, [id]: false }))
    }
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  const handleEdit = (id) => {
    setShowModalUpdate((show) => !show)
    setEditId(id)
  }

  const handleEditBatch = (id) => {
    setShowModalEdit((show) => !show)
    setEditId(id)
  }

  return (
    <div className="container-fluid px-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Master Batch</h3>
        </Col>
        <Col className="text-end">
          <Button
            className="btn btn-primary-orange shadow-sm rounded px-4 py-2"
            onClick={() => setShowModalAdd(true)}
          >
            <Plus size={16} className="me-2" />
            Add
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={12}>
            <Form.Group>
              <Form.Label
                className="text-uppercase fw-bold text-dark mb-2"
                style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
              >
                Search
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search by batch or item name..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{
                    height: '45px',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    fontSize: '0.9rem'
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row className="mt-3">
        <Col md={12}>
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
            <Table responsive hover className="mb-0 b-table-frameless">
              <thead style={tableHeaderStyle}>
                <tr>
                  <th className="p-2 align-middle text-center">No</th>
                  <th className="p-2 align-middle">Batch</th>
                  <th className="p-2 align-middle">Item Code</th>
                  <th className="p-2 align-middle">Item Name</th>
                  <th className="p-2 align-middle">Manufacturing Date</th>
                  <th className="p-2 align-middle">Exp. Date</th>
                  <th className="p-2 align-middle">Note</th>
                  <th className="p-2 align-middle text-center">COA</th>
                  <th className="p-2 align-middle text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && batches.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <RowEmpty colSpan={8} />
                    </td>
                  </tr>
                )}
                {!loading &&
                  batches?.map((item, i) => (
                    <TbodyRow
                      data={item}
                      key={item.id}
                      number={i + 1 + (current_page - 1) * per_page}
                      handleEdit={() => handleEdit(item.id)}
                      handleEditBatch={() => handleEditBatch(item.id)}
                      handleDownload={() => handleDownloadCoa(item.id)}
                      isDownloading={downloadingRows[item.id]}
                    />
                  ))}
              </tbody>
            </Table>
            {batches.length > 0 && (
              <div className="p-3 border-top bg-white">
                <TablePagination
                  meta={meta}
                  handlePageChange={handlePageChange}
                  handleOnChangeMeta={handleOnChangeMeta}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <ModalAddBatch
        show={showModalAdd}
        onHide={() => setShowModalAdd(false)}
        onAdd={() => setIsAdd((add) => !add)}
      />
      <ModalDetailBatch
        show={showModalUpdate}
        onHide={() => setShowModalUpdate(false)}
        data={editId}
        setIsUpdate={setIsUpdate}
      />
      <ModalEditBatch
        show={showModalEdit}
        onHide={() => setShowModalEdit(false)}
        data={editId}
        setIsUpdate={setIsUpdate}
      />
    </div>
  )
}

function TbodyRow({
  data,
  handleEdit,
  handleEditBatch,
  number,
  handleDownload,
  isDownloading
}) {
  return (
    <tr key={data.id}>
      <td className="p-2 text-center align-middle">{number}</td>
      <td className="p-2 align-middle fw-medium text-dark">{data.batch}</td>
      <td className="p-2 align-middle">{data.item?.item_code || '-'}</td>
      <td className="p-2 align-middle">{data.item?.item_name || '-'}</td>
      <td className="p-2 align-middle text-muted">
        {data.manufacturing_date
          ? convertDateWithoutTime(data.manufacturing_date)
          : '-'}
      </td>
      <td className="p-2 align-middle text-muted">
        {data.exp_date ? convertDateWithoutTime(data.exp_date) : '-'}
      </td>
      <td className="p-2 align-middle text-muted">{data.note ?? '-'}</td>
      <td className="p-2 align-middle text-center">
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="btn btn-light shadow-sm rounded d-inline-flex align-items-center justify-content-center p-0"
          style={{
            width: '32px',
            height: '32px'
          }}
          title="Download COA"
        >
          {isDownloading ? (
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: '13px', height: '13px', borderWidth: '2px' }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <Download size={13} color="#6c757d" />
          )}
        </button>
      </td>
      <td className="p-2 align-middle text-end">
        <TableAction>
          <button
            className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
            style={{ width: '32px', height: '32px' }}
            onClick={handleEdit}
            title="Detail"
          >
            <Search size={13} />
          </button>
          <button
            className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0 ms-1"
            style={{ width: '32px', height: '32px' }}
            onClick={handleEditBatch}
            title="Edit"
          >
            <Pencil size={13} />
          </button>
        </TableAction>
      </td>
    </tr>
  )
}

export default MasterBatchPage
